import clsx from 'clsx';
import React, { forwardRef } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import { BsGrid3X3Gap, BsGrid, BsSquare, BsFillCaretDownFill } from 'react-icons/bs';

import BayAlphabeticalFilter, { type BayAlphabeticalFilterProps } from '../BayAlphabeticalFilter';
import BayTooltip from '../BayTooltip';
import AgendaView from '../Icons/AgendaView';
import CardsIcon from '../Icons/CardsIcon';
import FilterIcon from '../Icons/FilterIcon';
import GridIcon from '../Icons/GridIcon';

import './BayDataGridActionsBar.scss';

type AlphabetFilter = Parameters<BayAlphabeticalFilterProps['onApplyAlphabetFilter']>[0];

interface CustomToggleProps {
	children: React.ReactNode;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
}

const CustomToggle = forwardRef<HTMLButtonElement, CustomToggleProps>(({ children, onClick, disabled }, ref) => (
	<Button
		ref={ref}
		className="status-selected-label btn-sm text-dark fw-bold fs-6 p-0 my-0 lh-1 text-decoration-none d-inline-flex align-items-center justify-content-center border-0"
		disabled={disabled}
		variant="link"
		onClick={(e) => {
			e.preventDefault();
			e.stopPropagation();
			onClick(e);
		}}
	>
		<span className="bay-sort-label-text text-truncate d-inline-block align-middle">{children}</span>
		<span className="ms-1 text-700 d-inline-flex align-items-center lh-1">
			<BsFillCaretDownFill size={16} />
		</span>
	</Button>
));

CustomToggle.displayName = 'CustomToggle';

interface DropdownOption {
	label: string;
	value: string | number;
}

interface BayDataGridActionsBarProps {
	setModal: (value: boolean) => void;
	isLoading?: boolean;
	filterCount?: number;
	isSwitchOn: boolean | string;
	setIsSwitchOn: (value: boolean | string) => void;
	showSwitchView?: boolean;
	showColumnVisibility?: boolean;
	showFilterButton?: boolean;
	t: (key: string) => string;
	portalElementId?: string;
	SearchComponent?: React.ReactNode;
	PageSizeSelectorComponent?: React.ReactNode;
	handleToggleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	showRadioSwitch?: boolean;
	isMobileView?: boolean;
	filterBarClassName?: string;
	searchWrapperClassName?: string;
	showSortedFilter?: boolean;
	dropdownOptions?: DropdownOption[];
	sortedOption?: number;
	setSortedOption?: (index: number) => void;
	isGridIcons?: boolean;
	setModalState?: (state: { action: string; filters: any; active: boolean }) => void;
	showAlphabetFilter?: boolean;
	setAlphabeticFilter?: (filter: AlphabetFilter) => void;
	alphabeticField?: string;
	alphabeticLetter?: string;
	/** Replaces the default `my-3 py-2` spacing on the wrapper. */
	className?: string;
}

const BayDataGridActionsBar: React.FC<BayDataGridActionsBarProps> = ({
	setModal,
	isLoading = false,
	isSwitchOn,
	setIsSwitchOn,
	showSwitchView = true,
	showColumnVisibility = true,
	showFilterButton = true,
	filterCount = 0,
	SearchComponent,
	PageSizeSelectorComponent,
	portalElementId = 'visibility-component',
	t,
	showRadioSwitch = false,
	isMobileView = false,
	handleToggleChange = () => {},
	showSortedFilter = false,
	dropdownOptions = [],
	sortedOption = 0,
	filterBarClassName = '',
	searchWrapperClassName = '',
	setSortedOption = () => {},
	isGridIcons = false,
	setModalState,
	showAlphabetFilter = false,
	setAlphabeticFilter,
	alphabeticField = '',
	alphabeticLetter = '',
	className = '',
}) => {
	const toggleComponent = () => {
		return (
			// `d-none d-lg-flex` when hidden, plain `d-flex` otherwise -- the display
			// keyword has to be part of the CONDITIONAL, not sit alongside it.
			// `d-flex ... d-none d-lg-block` was self-cancelling: utilities compile
			// `!important` in this build, base `.d-*` emit in $display order so
			// `d-flex` lands after `d-none` and won at every width (the row was never
			// hidden), and responsive tiers emit after all base utilities so
			// `d-lg-block` then beat `d-flex` and killed `align-items-center` >= 992px.
			<div className={`${isGridIcons ? 'd-none d-lg-flex' : 'd-flex'} align-items-center mb-3 mb-sm-0`}>
				<div className="switch-toggle">
					{!isMobileView && (
						<>
							<input
								checked={isSwitchOn === 'stacked'}
								id="stacked"
								name="state-d"
								type="radio"
								value="stacked"
								onChange={handleToggleChange}
							/>
							<label className="toggle-agenda-view" htmlFor="stacked">
								<BayTooltip overlay={t(isGridIcons ? 'smallGrid' : 'horizontalLayout')} placement="top">
									{isGridIcons ? <BsGrid3X3Gap className={isSwitchOn === 'stacked' ? '' : 'text-700'} size={16} /> : <AgendaView />}
								</BayTooltip>
							</label>
						</>
					)}
					<input
						checked={isSwitchOn === 'grid'}
						id="grid"
						name="state-d"
						type="radio"
						value="grid"
						onChange={handleToggleChange}
					/>
					<label className="toggle-grids-view" htmlFor="grid">
						<BayTooltip overlay={t(isGridIcons ? 'standardGrid' : 'listLayout')} placement="top">
							{isGridIcons ? <BsGrid className={isSwitchOn === 'grid' ? '' : 'text-700'} size={16} /> : <GridIcon />}
						</BayTooltip>
					</label>
					<input
						checked={isSwitchOn === 'card'}
						id="card"
						name="state-d"
						type="radio"
						value="card"
						onChange={handleToggleChange}
					/>
					<label className="toggle-cards-view" htmlFor="card">
						<BayTooltip overlay={t(isGridIcons ? 'largeGrid' : 'gridLayout')} placement="top">
							{isGridIcons ? <BsSquare className={isSwitchOn === 'card' ? '' : 'text-700'} size={16} /> : <CardsIcon />}
						</BayTooltip>
					</label>
				</div>
			</div>
		);
	};

	const visibilityColumnClasses = clsx({
		'd-none': isMobileView,
		'd-flex': !isMobileView,
	});

	const filterButtonClasses = clsx(
		filterBarClassName,
		'd-flex align-items-center flex-1 justify-content-between',
	);

	const searchWrapperClasses = clsx(
		'd-flex flex-wrap flex-sm-nowrap search-wrap p-0 my-3 my-lg-0 ms-lg-3',
		searchWrapperClassName,
	);

	const openCreateViewModal = (filters: any) => {
		if (setModalState) {
			setModalState({
				action: 'create',
				filters,
				active: true,
			});
		}
	};

	return (
		<div className={clsx('bay-data-grid-actions-bar', className || 'my-3 py-2')}>
			<div
				className={`bay-data-grid-wrapper d-flex flex-sm-row align-items-center flex-wrap flex-lg-nowrap action-bar-sm-wrap ${showFilterButton || showSortedFilter ? 'justify-content-between' : 'justify-content-end'}`}
			>
				<div className={filterButtonClasses}>
					<div className="d-flex align-items-center justify-content-start">
						{showFilterButton && (
							<Button
								className={`filter-btn align-items-center ${isMobileView ? 'me-2' : 'me-3'} ${filterCount && filterCount > 0 ? 'has-filter-count' : ''}`}
								disabled={isLoading}
								variant="outline-secondary"
								onClick={() => setModal(true)}
							>
								<FilterIcon className="me-2" size="1em" />
								<span className="d-block d-md-inline">
									{t('filter')}
									{filterCount && filterCount > 0
										? ` | ${filterCount <= 10 ? filterCount : '10+'}`
										: false}
								</span>
							</Button>
						)}
						{showColumnVisibility && <div className={`d-none d-sm-flex me-3 ${visibilityColumnClasses}`} id={portalElementId} />}
						{showSortedFilter && dropdownOptions?.length > 0 &&
							<div className="d-inline-flex flex-nowrap align-items-center gap-2" id="baySortBy">
								<span className="text-600 mb-0 lh-1">Sort By:</span>
								<Dropdown className="d-inline-flex align-items-center">
									<Dropdown.Toggle
										as={CustomToggle}
										id="baySortByDropdown"
									>
										{dropdownOptions[sortedOption]?.label}
									</Dropdown.Toggle>
									<Dropdown.Menu>
										{dropdownOptions?.map((option, index) => (
											<Dropdown.Item key={option?.value} className={`${sortedOption === index ? 'fw-bold bg-blue-100' : ''}`} onClick={() => setSortedOption(index)}>{option?.label}</Dropdown.Item>
										))}
									</Dropdown.Menu>
								</Dropdown>
							</div>
						}
						{showAlphabetFilter && (
							<div className="d-flex align-items-center gap-2">
								<BayAlphabeticalFilter
									isLoading={isLoading}
									selectedFieldValue={alphabeticField}
									selectedLetterValue={alphabeticLetter}
									t={t}
									onApplyAlphabetFilter={setAlphabeticFilter}
									onSaveView={openCreateViewModal}
								/>
							</div>
						)}
					</div>
					{showSwitchView && !showRadioSwitch && (
						<label className={`switch me-0 ${isLoading ? 'pe-none opacity-50' : ''}`}>
							<input
								checked={Boolean(isSwitchOn)}
								type="checkbox"
								onChange={() => setIsSwitchOn(!isSwitchOn)}
							/>
							<div className="slider">
								<BayTooltip overlay={t('gridLayout')} placement="top">
									<div>
										<CardsIcon className="cards-view" />
									</div>
								</BayTooltip>
								<BayTooltip overlay={t('listLayout')} placement="top">
									<div>
										<GridIcon className="grids-view" />
									</div>
								</BayTooltip>
							</div>
						</label>
					)}
					{showSwitchView &&
						showRadioSwitch &&
						!isMobileView &&
						toggleComponent()}
				</div>
				<div>
					{showSwitchView &&
						showRadioSwitch &&
						isMobileView &&
						toggleComponent()}
				</div>
				<div className={searchWrapperClasses}>
					{PageSizeSelectorComponent ? PageSizeSelectorComponent : false}
					{SearchComponent ? SearchComponent : false}
				</div>
			</div>
		</div>
	);
};

export default BayDataGridActionsBar;
